
<script>
  import { mapGetters, mapMutations } from 'vuex'
  import GlobalLoading from '@/mixins/GlobalLoading'
  import EntityProxy from '@/models/EntityProxy'

  export default {
    computed: {
      ...mapGetters(['getMapMeta', 'getMapModel', 'getMapModels']),
      changeQuery() {
        const values = Object.entries(this.query)
          .flat(1).sort().filter(v => this.$utils.isNotEmpty(v)).join(',')
        const oldValues = Object.entries(this.previousQuery)
          .flat(1).sort().filter(v => this.$utils.isNotEmpty(v)).join(',')

        return values !== oldValues
      },
      changeQueryAndHasNumberOfBadges() {
        return this.numberOfBadges && this.changeQuery
      },
      currentAxios() {
        return this.axios
      },
      getMeta() {
        if (this.withStore)
          return this.getMapMeta(this.currentKey())
        else
          return this.meta
      },
      getModel() {
        if (this.withStore)
          return this.getMapModel(this.currentKey())
        else
          return this.model
      },
      getModels() {
        if (this.withStore)
          return this.getMapModels(this.currentKey())
        else
          return this.models
      },
      hasFilter() {
        let filter = {...this.query}
        return Object.values(filter).some(v => {
          if (Array.isArray(v))
            return v.length > 0
          else
            return !!v
        })
      },
      infoTotalModels() {
        if (!this.meta.totalCount)
          return 'Nenhum registro'

        let page = this.meta.currentPage
        let perPage = this.meta.perPage
        let startPage = perPage * (page -1) + 1
        let lastPage = Math.min(perPage * page, this.meta.totalCount)
        return `Mostrando de ${startPage} até ${lastPage} de ${this.meta.totalCount} registro(s)`
      },
      withStore() {
        return this.defaultModelSetting.withStore
      }
    },
    created() {
      if (!this.nameModel)
        throw 'to use this Mixin, you must fill in the attribute: nameModel'

      if (!this.modelPath)
        this.modelPath = this.$parse.pluralize(this.$parse.toSnakecase(this.nameModel))

      let prefix = this.modelPathPrefix
      if (prefix)
        this.modelPath = `${prefix}/${this.modelPath}`
    },
    data() {
      return {
        defaultModelSetting: {
          camelcase: true,
          globalLoad: true,
          handleGeneric: true,
          updateQueryURL: false,
          withStore: false,
        },
        fields: [],
        includes: [],
        meta: {},
        methods: [],
        model: {},
        models: [],
        numberOfBadges: 0,
        order: [],
        params: {
          meta: true
        },
        previousQuery: {},
        query: {},
        selectFields: [],
        showFilter: false,
      }
    },
    methods: {
      ...mapMutations(['mapModel', 'mapModels', 'mapMeta', 'clearModelMapper']),
      addOrderBy(column, by) {
        const orderBy = by ?? 'desc'
        this.order.push(this.$parse.toSnakecase(column), orderBy)
      },
      async asyncDelete(model, headers) {
        const path = this.buildPath()
        return this.currentAxios.delete(`${path}/${model.id}`, { headers: headers})
      },
      async asyncSave(data, headers) {
        const path = this.buildPath()
        const id = this.model.id
        const method = id ? 'put' : 'post'
        const url = id ? `${path}/${this.model.id}` : path

        return await this.currentAxios[method](url, data, { headers: headers})
      },
      async asyncSelect(path, params, headers) {
        this.previousQuery = this.$parse.clone(this.query)
        return await this.currentAxios.get(path, { headers: headers, params: params })
      },
      async asyncShow(id, path, headers) {
        return this.currentAxios.get(`${path}/${id}`, { headers: headers })
      },
      async asyncTemplateDelete(model, { initialize, before, after, finish, error } = {}) {
        if (model && typeof model === 'object')
          this.setModel(model)

        try {
          if (typeof initialize === 'function')
            initialize()

          this.openLoad()
          const headers = this.buildHeaders()

          if (typeof before === 'function')
            before({headers, model})

          const response = await this.asyncDelete(model, headers)
          if (typeof after === 'function')
            after({ ...response, model: model })

        } catch (e) {
          this.$notify.addError(e)
          if (typeof error === 'function') error(e)

        } finally {
          this.closeLoad()
          if (typeof finish === 'function') finish(model)
        }

        return model
      },
      async asyncTemplateSave(model, { initialize, before, after, finish, error } = {}) {
        if (model && typeof model === 'object')
          this.setModel(model)

        try {
          if (typeof initialize === 'function')
            initialize()

          this.openLoad()
          const data = this.buildData(model)
          const headers = this.buildHeaders()

          if (typeof before === 'function')
            before({data, headers})

          const response = await this.asyncSave(data, headers)

          if (this.defaultModelSetting.camelcase)
            model = this.dataToCamelcase(response.data)
          else
            model = response.data
          this.setModel(model)

          if (typeof after === 'function')
            after({ ...response, model: model })

        } catch (e) {
          this.$notify.addError(e)
          if (typeof error === 'function') error(e)

        } finally {
          this.closeLoad()
          if (typeof finish === 'function') finish(model)
        }

        return model
      },
      async asyncTemplateSelect({ pagination, byURL, initialize, before, after, finish, error } = {}) {
        let models
        let meta

        try {
          if (typeof initialize === 'function')
            await initialize()

          this.openLoad()

          if (byURL)
            this.setQueryByUrl(this.query)

          const params = this.buildParams(pagination)
          const headers = this.buildHeaders()
          const path = this.buildPath()

          if (typeof before === 'function')
            await before({headers, params})

          const response = await this.asyncSelect(path, params, headers)

          if (this.defaultModelSetting.camelcase) {
            models = this.dataToCamelcase(response.data)
            meta = this.metaToCamelcase(response.meta)
          } else {
            models = response.data
            meta = response.meta
          }
          this.setModels(models)
          this.setMeta(meta)
          this.updateQueryURL(params)

          if (typeof after === 'function')
            await after({ ...response, models: models })

        } catch (e) {
          this.$notify.addError(e)
          if (typeof error === 'function') await error(e)

        } finally {
          this.closeLoad()
          if (typeof finish === 'function') await finish(models)
        }

        return models
      },
      async asyncTemplateShow(id, {initialize, before, after, finish, error}={}) {
        let model = { id: id }
        try {
          if (typeof initialize === 'function')
            await initialize()

          this.openLoad()
          const headers = this.buildHeaders()
          const path = this.buildPath()

          if (typeof before === 'function')
            await before({headers, model, path})

          const response = await this.asyncShow(id, path, headers)

          if (this.defaultModelSetting.camelcase)
            model = this.dataToCamelcase(response.data)
          else
            model = response.data
          this.setModel(model)

          if (typeof after === 'function')
            await after({ ...response, model: model })

        } catch (e) {
          this.$notify.addError(e)
          if (typeof error === 'function') await error(e)

        } finally {
          this.closeLoad()
          if (typeof finish === 'function') await finish(model)
        }

        return model
      },
      buildData(model) {
        return this.$parse.toSnakecase(model)
      },
      buildHeaders() {
        return {}
      },
      buildParams(pagination) {
        if (!pagination)
          delete this.params.page

        let params = {...this.params }
        if (this.order.length > 0)
          params.o = [...this.order]

        if (Object.keys(this.query).length > 0)
          params.q = { ...this.query }

        if (this.defaultModelSetting.handleGeneric && params.q && params.q.generic)
          params.q.generic = encodeURIComponent(params.q.generic)

        if (this.fields.length > 0)
          params.f = [...this.fields]

        if (this.methods.length > 0)
          params.methods = [...this.methods]

        if (this.includes.length > 0)
          params.include = [...this.includes]

        params = this.$parse.sanitize(params)
        this.$parse.fixDate(params)
        return this.$parse.toSnakecase(params)
      },
      buildPath() {
        return this.modelPath
      },
      clearQuery(find, callback) {
        Object.keys(this.query).forEach(key =>  this.query[key] = '')

        if (find){
          this.asyncTemplateSelect()
        } else if (this.defaultModelSetting.updateQueryURL) {
          const params = this.buildParams()
          this.updateQueryURL(params)
        }
        if (typeof callback === 'function') callback()
      },
      clearQueryAndFind(callback) {
        this.clearQuery(true, callback)
      },
      closeLoad() {
        this.closeGlobalLoad()
      },
      currentKey() {
        return this.keyModel || this.nameModel
      },
      openLoad() {
        this.openGlobalLoad()
      },
      pagination(page) {
        this.params.page = page
        this.asyncTemplateSelect({ pagination: true })
      },
      setMeta(meta) {
        this.meta = meta
        if (this.withStore)
          this.mapMeta({meta: meta, resource: this.currentKey()})
      },
      setModel(model) {
        this.model = model
        if (this.withStore)
          this.mapModel({model: model, resource: this.currentKey()})
      },
      setModels(models) {
        this.models = models
        if (this.withStore)
          this.mapModels({models: models, resource: this.currentKey()})
      },
      setQueryByUrl(currentParams, camelcase = false) {
        this.$utilUri.getQuery({
          callback: params => {
            const query = params['query'] || params['q'] || {}

            Object.entries(query).forEach(([key, value]) => {
              const newKey = this.toCamelcase(key)
              if (key.toLocaleLowerCase().endsWith('date') && value)
                this.$set(this.query, newKey, value.substring(0, 10))
              else if (value)
                this.$set(this.query, newKey, value)
            })
          },
          camelcase: camelcase
        })
      },
      dataToCamelcase(data) {
        return EntityProxy.build(data)
      },
      metaToCamelcase(meta) {
        return EntityProxy.build(meta)
      },
      toCamelcase(payload) {
        return this.$parse.toCamelcase(payload)
      },
      updateNumberOfBadges(amount) {
        this.numberOfBadges = amount
      },
      updateQueryURL(params) {
        if (this.defaultModelSetting.updateQueryURL)
          this.$utilUri.updateQuery(params)
      }
    },
    mixins: [GlobalLoading],
    name: 'ModelMapper'
  }

</script>
